import * as React from 'react';
import { st, classes } from './ToggleSwitchCore.st.css';
import { dataHooks } from './constants';
import { filterDataProps } from './utils/filter-data-props';
import {
  StatusContext,
  getAriaAttributesFromContext,
} from '../../FormField/StatusContext';

// The only reason this exists is that Santa currently doesn't support boolean and number types
// in the style panel, and some of the styling options have to live in the layout panel,
// and we pass them down as inline styles.
export interface ToggleSwitchCoreStyles {
  root?: React.CSSProperties;
  track?: React.CSSProperties;
  knob?: React.CSSProperties;
  knobIcon?: React.CSSProperties;
}

export interface ToggleSwitchCoreProps {
  /** hook for testing purposes */
  'data-hook'?: string;
  className?: string;
  checked?: boolean;
  disabled?: boolean;
  tabIndex?: number;
  onChange?(): void;
  styles?: ToggleSwitchCoreStyles;
  id?: string;
  checkedIcon?: React.ReactNode;
  uncheckedIcon?: React.ReactNode;
  dataHook?: string;
  'aria-label'?: string;
}

export interface ToggleSwitchCoreState {
  focus: boolean;
  focusVisible: boolean;
}

/**
 * Toggle Switch
 */
export default class ToggleSwitchCore extends React.PureComponent<
  ToggleSwitchCoreProps,
  ToggleSwitchCoreState
> {
  static displayName = 'ToggleSwitchCore';

  static defaultProps = {
    checked: false,
    styles: {},
    tabIndex: 0,
    onChange: () => null,
  };

  public state = {
    focus: false,
    focusVisible: false,
  };

  // We don't want to show outline when the component is focused by mouse.
  private focusedByMouse = false;

  render() {
    const {
      checked,
      disabled,
      styles: inlineStyles,
      className,
      dataHook,
    } = this.props;

    return (
      <StatusContext.Consumer>
        {statusContext => (
          <div
            className={st(
              classes.root,
              {
                checked,
                disabled,
                focus: this.state.focus,
                'focus-visible': this.state.focusVisible,
              },
              className,
            )}
            style={inlineStyles?.root}
            data-disabled={disabled}
            data-hook={dataHook}
            {...filterDataProps(this.props)}
          >
            <div
              data-hook={dataHooks.track}
              className={classes.track}
              style={inlineStyles?.track}
            />
            <div
              data-hook={dataHooks.knob}
              className={classes.knob}
              style={inlineStyles?.knob}
            >
              <div
                data-hook={dataHooks.knobIcon}
                className={classes.knobIcon}
                style={inlineStyles?.knobIcon}
              >
                {checked ? this.props.checkedIcon : this.props.uncheckedIcon}
              </div>
            </div>
            <input
              id={this.props.id}
              className={classes.input}
              type="checkbox"
              checked={checked}
              readOnly={disabled}
              tabIndex={disabled ? -1 : this.props.tabIndex}
              onChange={disabled ? undefined : this.props.onChange}
              onFocus={disabled ? undefined : this.handleFocus}
              onBlur={disabled ? undefined : this.handleBlur}
              onMouseDown={this.handleMouseDown}
              onKeyDown={this.handleKeyDown}
              aria-label={this.props['aria-label']}
              data-hook={dataHooks.toggleSwitchInput}
              {...getAriaAttributesFromContext(statusContext)}
            />
          </div>
        )}
      </StatusContext.Consumer>
    );
  }

  private readonly handleKeyDown: React.KeyboardEventHandler<HTMLElement> =
    () => {
      // Pressing any key should make the focus visible, even if the checkbox
      // was initially focused by mouse.
      this.setState({ focusVisible: true });
    };

  // Doesn't get invoked if the input is disabled.
  private readonly handleMouseDown: React.MouseEventHandler<HTMLElement> =
    e => {
      if (e.button === 0) {
        this.focusedByMouse = true;
      }
    };

  private readonly handleFocus: React.FocusEventHandler<HTMLElement> = () => {
    this.setState({ focus: true, focusVisible: !this.focusedByMouse });
  };

  private readonly handleBlur: React.FocusEventHandler<HTMLElement> = () => {
    this.setState({ focus: false, focusVisible: false });
    this.focusedByMouse = false;
  };
}
